import { useState, useEffect, useRef, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  MessageCircle, Send, Heart, Phone, BookOpen, AlertTriangle,
  Loader2, RefreshCw, Trash2, ChevronRight, HelpCircle,
  User, Bot, Clock, ArrowRight, Shield, Sparkles, FileText
} from "lucide-react";
import ExportToDoctor from "@/components/ExportToDoctor";
import { AIDisclaimerBanner } from "@/components/AIDisclaimer";

// Clean AI-generated text by removing formatting artifacts
const sanitizeAIText = (text) => {
  if (!text) return "";
  return text
    .replace(/\*\*/g, "")           // Remove bold markdown **
    .replace(/\*/g, "")             // Remove italic markdown *
    .replace(/#{1,6}\s*/g, "")      // Remove heading markdown #
    .replace(/`{1,3}/g, "")         // Remove code blocks
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1") // Convert links to text
    .replace(/\n{3,}/g, "\n\n")     // Reduce multiple newlines
    .replace(/^\s*[-•]\s*/gm, "• ") // Normalize bullet points
    .trim();
};

const STAGE_COLORS = {
  denial: "bg-gray-100 text-gray-700",
  anger: "bg-red-100 text-red-700",
  bargaining: "bg-yellow-100 text-yellow-700",
  depression: "bg-blue-100 text-blue-700",
  acceptance: "bg-green-100 text-green-700"
};

export default function GriefCounselorChat() {
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [currentConversation, setCurrentConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [resources, setResources] = useState([]);
  const [stages, setStages] = useState(null);
  const [emergencyResources, setEmergencyResources] = useState(null);
  const [showWelcome, setShowWelcome] = useState(true);
  const [suggestedResources, setSuggestedResources] = useState([]);
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const loadData = useCallback(async () => {
    try {
      const [statusRes, convRes, resourcesRes, stagesRes, emergencyRes] = await Promise.all([
        api.get("/phase72/status"),
        api.get("/phase72/conversations"),
        api.get("/phase72/resources"),
        api.get("/phase72/stages"),
        api.get("/phase72/emergency-resources")
      ]);
      
      setStatus(statusRes.data);
      setConversations(convRes.data.conversations || []);
      setResources(resourcesRes.data.resources || []);
      setStages(stagesRes.data);
      setEmergencyResources(emergencyRes.data);
    } catch (error) {
      console.error("Error loading grief counselor data:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const loadConversation = async (conversationId) => {
    try {
      const res = await api.get(`/phase72/conversations/${conversationId}`);
      setCurrentConversation(conversationId);
      // Sanitize all messages when loading
      const cleanedMessages = (res.data.messages || []).map(m => ({
        ...m,
        content: m.role === "assistant" ? sanitizeAIText(m.content) : m.content
      }));
      setMessages(cleanedMessages);
      setShowWelcome(false);
    } catch (error) {
      toast.error("Failed to load conversation");
    }
  };

  const startNewConversation = () => {
    setCurrentConversation(null);
    setMessages([]);
    setShowWelcome(false);
    setSuggestedResources([]);
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim() || sending) return;
    
    const userMessage = inputMessage.trim();
    setInputMessage("");
    setSending(true);
    
    // Add user message immediately
    const tempUserMsg = {
      role: "user",
      content: userMessage,
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, tempUserMsg]);
    
    try {
      const res = await api.post("/phase72/chat", {
        message: userMessage,
        conversation_id: currentConversation
      });
      
      // Update conversation ID if new
      if (!currentConversation) {
        setCurrentConversation(res.data.conversation_id);
      }
      
      // Add AI response
      const aiMessage = {
        role: "assistant",
        content: sanitizeAIText(res.data.response),
        timestamp: res.data.timestamp,
        detected_stage: res.data.detected_stage
      };
      setMessages(prev => [...prev, aiMessage]);
      
      // Show suggested resources
      if (res.data.suggested_resources?.length > 0) {
        setSuggestedResources(res.data.suggested_resources);
      }
      
      // Show crisis alert if detected
      if (res.data.crisis_detected) {
        toast.warning("Crisis resources have been provided. Please reach out for immediate support.", {
          duration: 10000
        });
      }
      
      // Refresh conversations list
      loadData();
      
    } catch (error) {
      toast.error("Failed to send message");
      // Remove the temp message on error
      setMessages(prev => prev.filter(m => m !== tempUserMsg));
    } finally {
      setSending(false);
    }
  };

  const deleteConversation = async (conversationId) => {
    try {
      await api.delete(`/phase72/conversations/${conversationId}`);
      toast.success("Conversation deleted");
      if (currentConversation === conversationId) {
        setCurrentConversation(null);
        setMessages([]);
        setShowWelcome(true);
      }
      loadData();
    } catch (error) {
      toast.error("Failed to delete conversation");
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    return date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="grief-counselor-loading">
        <div className="text-center space-y-4">
          <Loader2 className="w-12 h-12 animate-spin text-teal-600 mx-auto" />
          <p className="text-slate-600 font-medium">Preparing your support space...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white" data-testid="grief-counselor">
      {/* AI Disclaimer Banner */}
      <AIDisclaimerBanner />
      
      <div className="max-w-6xl mx-auto p-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                <Heart className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-900">Grief Support</h1>
                <p className="text-slate-600">A compassionate space to share your feelings</p>
              </div>
            </div>
            {/* Export to Doctor Button */}
            <ExportToDoctor 
              chatHistory={messages.map(m => ({
                role: m.role,
                content: m.content,
                timestamp: m.timestamp
              }))}
              patientName="Caregiver"
            />
          </div>
          
          {status && (
            <Badge className={`mt-3 ${status.available ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}`}>
              {status.available ? "AI Counselor Available" : "Limited Support Mode"}
            </Badge>
          )}
        </div>

        <div className="grid lg:grid-cols-4 gap-6">
          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-4">
            {/* New Chat Button */}
            <Button 
              className="w-full" 
              onClick={startNewConversation}
              data-testid="new-chat-btn"
            >
              <MessageCircle className="w-4 h-4 mr-2" />
              New Conversation
            </Button>

            {/* Conversations List */}
            <Card>
              <CardHeader className="py-3">
                <CardTitle className="text-sm font-medium">Recent Conversations</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <ScrollArea className="h-48">
                  {conversations.length > 0 ? (
                    conversations.map((conv) => (
                      <div 
                        key={conv.id}
                        className={`p-3 border-b cursor-pointer hover:bg-slate-50 flex items-center justify-between ${
                          currentConversation === conv.id ? "bg-purple-50" : ""
                        }`}
                        onClick={() => loadConversation(conv.id)}
                      >
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-900 truncate">
                            {conv.messages?.[0]?.content?.slice(0, 30) || "Conversation"}...
                          </p>
                          <p className="text-xs text-slate-500">
                            {new Date(conv.updated_at).toLocaleDateString()}
                          </p>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-slate-400 hover:text-red-500"
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteConversation(conv.id);
                          }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))
                  ) : (
                    <p className="p-3 text-sm text-slate-500 text-center">No conversations yet</p>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>

            {/* Emergency Resources */}
            <Card className="border-red-200 bg-red-50">
              <CardHeader className="py-3">
                <CardTitle className="text-sm font-medium text-red-800 flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  Crisis Support
                </CardTitle>
              </CardHeader>
              <CardContent className="p-3 pt-0 space-y-2">
                {emergencyResources?.crisis_resources?.slice(0, 2).map((resource, idx) => (
                  <div key={idx} className="text-sm">
                    <p className="font-medium text-red-900">{resource.name}</p>
                    <p className="text-red-700">{resource.contact}</p>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>

          {/* Main Chat Area */}
          <div className="lg:col-span-3">
            <Card className="h-[600px] flex flex-col">
              {/* Chat Header */}
              <CardHeader className="border-b py-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center">
                    <Bot className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <CardTitle className="text-base">Compassionate AI</CardTitle>
                    <CardDescription className="text-xs">Here to listen and support you</CardDescription>
                  </div>
                </div>
              </CardHeader>

              {/* Messages Area */}
              <ScrollArea className="flex-1 p-4">
                {showWelcome ? (
                  <div className="h-full flex flex-col items-center justify-center text-center p-6" data-testid="welcome-screen">
                    <div className="w-20 h-20 rounded-full bg-purple-100 flex items-center justify-center mb-6">
                      <Heart className="w-10 h-10 text-purple-600" />
                    </div>
                    <h2 className="text-xl font-bold text-slate-900 mb-2">Welcome to Grief Support</h2>
                    <p className="text-slate-600 max-w-md mb-6">
                      This is a safe, confidential space to share your feelings. I&apos;m here to listen without judgment 
                      and offer support as you navigate your grief journey.
                    </p>
                    <div className="grid grid-cols-2 gap-3 max-w-sm">
                      <Button variant="outline" onClick={startNewConversation} className="h-auto py-3">
                        <div className="text-center">
                          <MessageCircle className="w-5 h-5 mx-auto mb-1" />
                          <span className="text-sm">Start Talking</span>
                        </div>
                      </Button>
                      <Button variant="outline" className="h-auto py-3">
                        <div className="text-center">
                          <BookOpen className="w-5 h-5 mx-auto mb-1" />
                          <span className="text-sm">View Resources</span>
                        </div>
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4" data-testid="messages-container">
                    {/* Initial AI greeting if no messages */}
                    {messages.length === 0 && (
                      <div className="flex gap-3">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center flex-shrink-0">
                          <Bot className="w-4 h-4 text-white" />
                        </div>
                        <div className="bg-slate-100 rounded-2xl rounded-tl-none p-4 max-w-[80%]">
                          <p className="text-slate-700">
                            Hello. I&apos;m here to listen and support you through whatever you&apos;re feeling right now. 
                            There&apos;s no right or wrong way to express yourself here. What&apos;s on your mind today?
                          </p>
                        </div>
                      </div>
                    )}
                    
                    {messages.map((msg, idx) => (
                      <div 
                        key={idx} 
                        className={`flex gap-3 ${msg.role === "user" ? "flex-row-reverse" : ""}`}
                      >
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                          msg.role === "user" 
                            ? "bg-teal-600" 
                            : "bg-gradient-to-r from-purple-500 to-pink-500"
                        }`}>
                          {msg.role === "user" 
                            ? <User className="w-4 h-4 text-white" />
                            : <Bot className="w-4 h-4 text-white" />
                          }
                        </div>
                        <div className={`rounded-2xl p-4 max-w-[80%] ${
                          msg.role === "user"
                            ? "bg-teal-600 text-white rounded-tr-none"
                            : "bg-slate-100 text-slate-700 rounded-tl-none"
                        }`}>
                          <p className="whitespace-pre-wrap">{msg.content}</p>
                          <p className={`text-xs mt-2 ${msg.role === "user" ? "text-teal-200" : "text-slate-400"}`}>
                            {formatTime(msg.timestamp)}
                          </p>
                        </div>
                      </div>
                    ))}
                    
                    {/* Typing indicator */}
                    {sending && (
                      <div className="flex gap-3">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center">
                          <Bot className="w-4 h-4 text-white" />
                        </div>
                        <div className="bg-slate-100 rounded-2xl rounded-tl-none p-4">
                          <div className="flex gap-1">
                            <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                            <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                            <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                          </div>
                        </div>
                      </div>
                    )}
                    
                    <div ref={messagesEndRef} />
                  </div>
                )}
              </ScrollArea>

              {/* Suggested Resources */}
              {suggestedResources.length > 0 && (
                <div className="border-t p-3 bg-purple-50">
                  <p className="text-xs text-purple-700 mb-2">Suggested Resources:</p>
                  <div className="flex gap-2 flex-wrap">
                    {suggestedResources.map((resource) => (
                      <Badge 
                        key={resource.id} 
                        variant="outline" 
                        className="cursor-pointer hover:bg-purple-100"
                      >
                        {resource.title}
                        <ChevronRight className="w-3 h-3 ml-1" />
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Input Area */}
              {!showWelcome && (
                <form onSubmit={sendMessage} className="border-t p-4">
                  <div className="flex gap-2">
                    <Input
                      ref={inputRef}
                      value={inputMessage}
                      onChange={(e) => setInputMessage(e.target.value)}
                      placeholder="Share what's on your mind..."
                      className="flex-1"
                      disabled={sending}
                      data-testid="chat-input"
                    />
                    <Button type="submit" disabled={sending || !inputMessage.trim()}>
                      {sending ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Send className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                  <p className="text-xs text-slate-400 mt-2 text-center">
                    <Shield className="w-3 h-3 inline mr-1" />
                    Your conversations are private and confidential
                  </p>
                </form>
              )}
            </Card>
          </div>
        </div>

        {/* Resources Section */}
        <div className="mt-8">
          <h2 className="text-xl font-bold text-slate-900 mb-4">Support Resources</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="resources-grid">
            {resources.map((resource) => (
              <Card key={resource.id} className="hover:shadow-lg transition-shadow cursor-pointer">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                      <BookOpen className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <h3 className="font-medium text-slate-900">{resource.title}</h3>
                      <p className="text-sm text-slate-600 mt-1">{resource.description}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <Badge variant="outline" className="text-xs">{resource.type}</Badge>
                        <span className="text-xs text-slate-500">{resource.estimated_time}</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Grief Stages Info */}
        {stages && (
          <div className="mt-8">
            <h2 className="text-xl font-bold text-slate-900 mb-4">Understanding Grief</h2>
            <Card>
              <CardContent className="p-6">
                <p className="text-slate-600 mb-4">{stages.note}</p>
                <div className="grid md:grid-cols-5 gap-4">
                  {Object.entries(stages.stages).map(([key, stage]) => (
                    <div key={key} className="text-center">
                      <div className={`w-12 h-12 rounded-full mx-auto mb-2 flex items-center justify-center ${STAGE_COLORS[key]}`}>
                        <span className="text-lg font-bold">{stage.name[0]}</span>
                      </div>
                      <h4 className="font-medium text-slate-900">{stage.name}</h4>
                      <p className="text-xs text-slate-600 mt-1">{stage.description}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
